import { useState } from 'react';
import { Button, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import SolarIcon from 'components/icons/SolarIcon';
import SpinnerBlock from 'components/others/SpinnerBlock';
import { useAuth } from 'context/AuthContext';
import { isSellerUser } from 'utils/helpers/access/hasUserAccess';
import { useObjectCard } from '../context/ObjectCardProvider';
import { isOtherTeamIncome } from '../model/isOtherTeamIncome';
import OffExpendTeamInfo from '../OffExpendTeamInfo';
import ProposalShortageBlock from '../ProposalShortage';
import AddServiceModal from './AddServiceModal';
import ExpendTeamTableBody from './ExpendTeamTableBody/ExpendTeamTableBody';
import CheckedRowsMenu from './shared/CheckedRowsMenu';

interface ObjectTabServicesProps {
    mode?: 'services' | 'other';
}

const ObjectTabServices = ({ mode = 'services' }: ObjectTabServicesProps) => {
    const isOther = mode === 'other';
    const showExpendType = isOther;
    const { t } = useTranslation(['Objects']);
    const { auth } = useAuth();
    const showAllocation = !isSellerUser(auth?.user);
    const { vObject, isEditLocked } = useObjectCard();
    const [showAddServiceModal, setShowAddServiceModal] = useState(false);
    const [showSpinnerExpendTeam, setShowSpinnerExpendTeam] = useState(false);
    const [showExpendTeamInfo, setShowExpendInfo] = useState<number | false>(false);
    const [checkedIds, setCheckedIds] = useState<number[]>([]);
    const rowIds = (vObject?.expendTeams || [])
        .filter((expend) => (isOther ? isOtherTeamIncome(expend) : !isOtherTeamIncome(expend)))
        .map((expend) => expend.ID);
    const allChecked = rowIds.length > 0 && rowIds.every((id) => checkedIds.includes(id));

    return (
        <>
            <AddServiceModal
                show={showAddServiceModal}
                setShow={setShowAddServiceModal}
                teamId={vObject?.object?.teamId || null}
                isOther={isOther}
            />

            <OffExpendTeamInfo
                show={showExpendTeamInfo}
                setShow={setShowExpendInfo}
            />

            <div>
                <ProposalShortageBlock kind={isOther ? 'other' : 'services'} />

                {(checkedIds.length > 0 || !isEditLocked) && (
                    <div className="d-flex justify-content-end align-items-center gap-2">
                        <CheckedRowsMenu
                            kind={isOther ? 'other' : 'services'}
                            checkedIds={checkedIds}
                            setCheckedIds={setCheckedIds}
                            setBusy={setShowSpinnerExpendTeam}
                        />
                        {!isEditLocked && (
                            <div className="mb-2">
                                <Button
                                    className="py-2"
                                    size="sm"
                                    variant="primary"
                                    onClick={() => setShowAddServiceModal(true)}
                                >
                                    <SolarIcon name="people" className="me-2" />
                                    {t(isOther ? 'addService.btnAddService' : 'addService.btnAdd')}
                                </Button>
                            </div>
                        )}
                    </div>
                )}

                {showSpinnerExpendTeam && <SpinnerBlock onlyBlur={true} show={true} />}
                <div className="crm-table-wrap">
                    <Table className={`ExpandTeamTable ExpandTeamTable--services table-hover table${showExpendType ? ' ExpandTeamTable--with-type' : ''}${showAllocation ? ' ExpandTeamTable--alloc' : ''}`}>
                        <thead>
                            <tr className="bg-light">
                                <td className="head-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        disabled={rowIds.length === 0}
                                        checked={allChecked}
                                        onChange={(e) => {
                                            setCheckedIds((prev) => {
                                                if (e.target.checked) {
                                                    return Array.from(new Set([...prev, ...rowIds]));
                                                }
                                                return prev.filter((id) => !rowIds.includes(id));
                                            });
                                        }}
                                    />
                                </td>
                                <td className="head-service">{t('fieldsService.service')}</td>
                                {showExpendType ? (
                                    <td className="head-expend-type text-truncate">{t('fieldsService.expendType')}</td>
                                ) : null}
                                <td className="head-price">{t('productFields.price')}</td>
                                <td className="head-count">{t('productFields.count')}</td>
                                <td className="head-amount">{t('productFields.amount')}</td>
                                {showAllocation ? (
                                    <td className="head-share">{t('fieldsService.allocation')}</td>
                                ) : null}
                                <td className="head-actions">{t('fieldsService.actions')}</td>
                            </tr>
                        </thead>
                        <ExpendTeamTableBody
                            checkedIds={checkedIds}
                            setCheckedIds={setCheckedIds}
                            isOther={isOther}
                            showExpendType={showExpendType}
                            showAllocation={showAllocation}
                            setShowExpendInfo={setShowExpendInfo}
                            setShowSpinnerExpendTeam={setShowSpinnerExpendTeam}
                        />
                    </Table>
                </div>
            </div>
        </>
    );
};

export default ObjectTabServices;
